import { create } from "zustand";
import { denoiserBuilt } from "@shared/denoiser/load";
import { isDesktop } from "@shared/lib/desktop";

/**
 * Обработка своего микрофона: шумодав, эхоподавление, автоусиление.
 *
 * Все три делает WebRTC внутри браузера, и все три `livekit-client` включает
 * своими умолчаниями — молча, потому что ограничений мы не задавали вовсе.
 * Выключить их было нечем, а надо: человеку с хорошим микрофоном шумодав
 * срезает хвосты слов, автоусиление вытягивает фон в паузах, а музыканту
 * мешает и то и другое.
 *
 * Держим здесь, а не в состоянии приложения: значение нужно в тот миг, когда
 * собирается комната, то есть вне отрисовки. Ровно как громкость.
 */

const KEY = "airel.audio";

/**
 * Степень шумоподавления.
 *
 * Одна шкала, а не два переключателя рядом: «своя модель» и «штатное
 * подавление» — не независимые свойства, а два ответа на один вопрос,
 * и вместе они не складываются. Два шумодава подряд дают не вдвое чище, а
 * съеденные согласные.
 */
export type Suppression =
  /** Ничего: комната тихая, техника не нужна. */
  | "off"
  /** Штатное подавление платформы — даром и без задержки. */
  | "platform"
  /** Своя модель: чище, но дороже — девять мегабайт и добавленная задержка. */
  | "model";

/**
 * Как открывается микрофон.
 *
 * «По кнопке» — рация (`push.ts`). Клавишу слышит только окно Airel, как и
 * веб-версия Discord: перехвата поверх игры у оболочки пока нет.
 */
export type InputMode = "voice" | "push";

export interface AudioTuning {
  suppression: Suppression;
  /** Эхоподавление. Выключать стоит только при наушниках с микрофоном. */
  echo: boolean;
  /** Автоматическая регулировка усиления. */
  gain: boolean;
  /** Усиление своего микрофона. Единица — как есть. */
  micVolume: number;
  /** Коррекция тембра, 0..1: убирает бубнение и подчёркивает разборчивость. */
  clarity: number;
  mode: InputMode;
  /**
   * Порог открытия микрофона, 0..1. Ноль — «авто»: не гасим ничего и
   * полагаемся на то, что говорящего определяет сервер.
   */
  threshold: number;
  /** Клавиша рации — `KeyboardEvent.code` или «Mouse4»; `null` — не задана. */
  pushKey: string | null;
  /** Задержка отпускания рации, мс: хвост слова после отпущенной клавиши. */
  pushDelay: number;
  /**
   * Своя обработка, запомненная при переходе на готовый профиль ввода:
   * «Пользовательский» возвращает её. `null` — запоминать было нечего.
   */
  custom: Processing | null;
}

/** Обработка, которую задаёт профиль ввода. */
export type Processing = Pick<AudioTuning, "suppression" | "echo" | "gain">;

/** Задержка отпускания рации — до двух секунд, как в Discord. */
export const MAX_PUSH_DELAY = 2000;

/**
 * Эхоподавление включено. Автоусиление — только по выбору человека:
 * на проверенном микрофоне оно давало скачки громкости даже при micVolume=1.
 * Сохранённый выбор ниже остаётся в силе.
 *
 * А вот степень различается по сборке. В оболочке страница лежит на диске, и
 * девять мегабайт там не стоят ничего; в вебе их пришлось бы везти по
 * проводу на каждую загрузку, а штатное подавление уже работает и достаётся
 * даром.
 */
const DEFAULTS: AudioTuning = {
  suppression: isDesktop() ? "model" : "platform",
  echo: true,
  gain: false,
  micVolume: 1,
  // Мягкая коррекция: 90 Гц, −3 дБ около 180 Гц, +2 дБ около 3 кГц.
  clarity: 0.5,
  mode: "voice",
  threshold: 0,
  pushKey: null,
  // Двадцать миллисекунд — умолчание Discord: почти сразу, но без щелчка.
  pushDelay: 20,
  custom: null,
};

/** Настройка по умолчанию — для «Сбросить» в голосе и видео. */
export function defaultAudioTuning(): AudioTuning {
  return { ...DEFAULTS };
}

/** Насколько можно прибавить свой микрофон. Два — это плюс шесть децибел. */
export const MAX_MIC_VOLUME = 2;

/** Как настройка выглядела до того, как два переключателя стали одной шкалой. */
interface OldTuning {
  noise?: boolean;
  model?: boolean;
}

/**
 * Читает сохранённое, переводя старую запись в новую.
 *
 * Перевод, а не сброс к умолчанию: человек уже отвечал на этот вопрос, и
 * заставлять его отвечать снова из-за того, что мы переставили органы
 * управления, — невежливо.
 */
function read(): AudioTuning {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };

    const saved = JSON.parse(raw) as Partial<AudioTuning> & OldTuning;

    // Поля перечислены поимённо, а не разложены целиком: иначе ключи старой
    // записи переехали бы в новую и остались в хранилище навсегда.
    return {
      suppression:
        saved.suppression ??
        (saved.model ? "model" : saved.noise === false ? "off" : "platform"),
      echo: saved.echo ?? DEFAULTS.echo,
      gain: saved.gain ?? DEFAULTS.gain,
      micVolume: saved.micVolume ?? DEFAULTS.micVolume,
      clarity:
        typeof saved.clarity === "number" && Number.isFinite(saved.clarity)
          ? Math.max(0, Math.min(1, saved.clarity))
          : DEFAULTS.clarity,
      mode: saved.mode ?? DEFAULTS.mode,
      threshold: saved.threshold ?? DEFAULTS.threshold,
      pushKey: typeof saved.pushKey === "string" ? saved.pushKey : DEFAULTS.pushKey,
      // На сетку ползунка, 20 мс: значение мимо неё ползунок сдвинул бы сам при открытии.
      pushDelay:
        typeof saved.pushDelay === "number" && Number.isFinite(saved.pushDelay)
          ? Math.max(0, Math.min(MAX_PUSH_DELAY, Math.round(saved.pushDelay / 20) * 20))
          : DEFAULTS.pushDelay,
      custom:
        saved.custom && typeof saved.custom === "object"
          ? {
              suppression: saved.custom.suppression ?? DEFAULTS.suppression,
              echo: saved.custom.echo ?? DEFAULTS.echo,
              gain: saved.custom.gain ?? DEFAULTS.gain,
            }
          : null,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

/** Стором zustand, а не своим списком подписчиков: см. `volume.ts`. */
const useTuning = create<AudioTuning>()(() => read());

export function audioTuning(): AudioTuning {
  return useTuning.getState();
}

export function setAudioTuning(next: AudioTuning): void {
  // Целиком, а не подмешиванием: настройка приходит собранной, и ключ,
  // которого в ней нет, остаться не должен.
  useTuning.setState(next, true);

  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Приватный режим и переполненное хранилище. Настройка доживёт до
    // перезагрузки в памяти — это лучше, чем уронить окно настроек.
  }
}

/** Подписка для экрана настроек. */
export function useAudioTuning(): [AudioTuning, (next: AudioTuning) => void] {
  return [useTuning(), setAudioTuning];
}

/**
 * Профиль ввода — как в Discord: два готовых набора обработки и своя
 * настройка.
 *
 * Профиль не хранится, а читается по самой обработке. Так меню и «Голос и
 * видео» не могут разойтись: тронул переключатель в настройках — профиль
 * сам стал «Пользовательским».
 */
export type InputProfile = "isolation" | "studio" | "custom";

const PROFILES: Record<Exclude<InputProfile, "custom">, Processing> = {
  /*
   * Самое чистое, что есть в этой сборке, — то же, что по умолчанию, — и
   * эхоподавление. Автоусиление выключено и здесь: на проверенном микрофоне
   * оно давало скачки громкости.
   */
  isolation: { suppression: DEFAULTS.suppression, echo: true, gain: false },
  /** Микрофон как есть: ни шумодава, ни эхоподавления, ни усиления. */
  studio: { suppression: "off", echo: false, gain: false },
};

function processingOf(value: AudioTuning): Processing {
  return { suppression: value.suppression, echo: value.echo, gain: value.gain };
}

export function inputProfile(value: AudioTuning = audioTuning()): InputProfile {
  for (const name of ["isolation", "studio"] as const) {
    const set = PROFILES[name];
    if (value.suppression === set.suppression && value.echo === set.echo && value.gain === set.gain) {
      return name;
    }
  }
  return "custom";
}

/**
 * Переход на профиль. Своя обработка перед заменой запоминается, и
 * «Пользовательский» её возвращает.
 *
 * Отвечает `false`, если вернуть нечего — своей обработки ещё не было: тогда
 * её заводят в настройках голоса.
 */
export function setInputProfile(profile: InputProfile): boolean {
  const now = audioTuning();
  const current = inputProfile(now);
  if (profile === current) return true;

  if (profile === "custom") {
    if (!now.custom) return false;
    setAudioTuning({ ...now, ...now.custom });
    return true;
  }

  const custom = current === "custom" ? processingOf(now) : now.custom;
  setAudioTuning({ ...now, ...PROFILES[profile], custom });
  return true;
}

/** Берётся ли в дело своя модель: и попросили, и в этой сборке она есть. */
export function modelWanted(value: AudioTuning = audioTuning()): boolean {
  return value.suppression === "model" && denoiserBuilt();
}

/**
 * Ставится ли выравниватель: тихий микрофон поднимается до обычной речи
 * (`@shared/denoiser/leveler.js`).
 *
 * На любой ступени подавления, кроме «Выключено». Та ступень — для тех, кому
 * микрофон нужен как есть, и туда же ведёт профиль «Студия»: музыканту
 * подтягивание тихих мест так же ни к чему, как шумодав.
 *
 * С браузерным автоусилением он не спорит: то стоит раньше, в захвате, и
 * поднятый им голос выравниватель уже не трогает — поднимает он только тихий.
 */
export function levelWanted(value: AudioTuning = audioTuning()): boolean {
  return value.suppression !== "off";
}

/**
 * Ограничения в том виде, в каком их ждёт `livekit-client`.
 *
 * `voiceIsolation` идёт вместе со штатным шумодавом: это то же подавление
 * платформы, только сильнее, и порознь они не нужны.
 *
 * Под моделью платформенное выключается целиком — она его заменяет, а не
 * дополняет. Эхоподавление и автоусиление при этом остаются на всех
 * ступенях: они про другое, и делать их своими руками мы не подписывались.
 */
export function captureConstraints(value: AudioTuning = audioTuning()) {
  // Модель, которой нет в этой сборке, читается как «обычное»: просили самое
  // чистое — даём самое чистое из того, что тут есть.
  const platform =
    value.suppression === "platform" ||
    (value.suppression === "model" && !denoiserBuilt());

  return {
    noiseSuppression: platform,
    voiceIsolation: platform,
    echoCancellation: value.echo,
    autoGainControl: value.gain,
  };
}

/**
 * Края ручного порога затвора, децибелы полной шкалы.
 *
 * Нижний край выбран так, чтобы годиться за «выключено»: −70 dBFS ниже
 * всего, что вообще приезжает с микрофона, и затвор на нём открыт всегда.
 * Своего положения «выключен» у ползунка нет, а нужно оно тем, кто играет
 * или поёт в микрофон, — тихие места такой человек терять не согласен.
 *
 * Верхний — −25 dBFS, уровень обычной речи: выше порог отсекал бы и её.
 */
const GATE_QUIET = -70;
const GATE_LOUD = -25;

/**
 * Порог затвора в долях полной шкалы: то, что понимает `gate.js`.
 *
 * Ноль наружу проходит нулём — это «авто», и считает его сам затвор по
 * шумовому полу. Дальше шкала ползунка ложится на децибелы ровно, а не на
 * доли: ухо слышит именно их, и на линейной шкале весь разговор уместился бы
 * в первой десятой ползунка.
 */
export function gateThreshold(value: AudioTuning = audioTuning()): number {
  // У рации порога нет, как в Discord: микрофон открывает клавиша, и тихую речь
  // с нажатой клавишей затвор резать не должен. Нижний край — «всегда открыт».
  if (value.mode === "push" && value.pushKey) return 10 ** (GATE_QUIET / 20);
  if (value.threshold <= 0) return 0;
  return 10 ** ((GATE_QUIET + value.threshold * (GATE_LOUD - GATE_QUIET)) / 20);
}

/**
 * Куда возвращаться, если модель не завелась.
 *
 * На ступень ниже, а не в тишину: просили самое чистое — значит из
 * доступного дать самое чистое. Штатное подавление тут и стоит первым.
 */
export function platformConstraints(value: AudioTuning = audioTuning()) {
  return captureConstraints({ ...value, suppression: "platform" });
}
